import {Autocomplete, Button, MenuItem, Select, SelectChangeEvent, TextField, Typography} from '@mui/material';
import {
  changeSelectedCommissionsReport,
  changeSelectedDateForReporting,
  changeSelectedProducerCodesForReporting,
  clearProducerFilter,
  CommissionsReport,
  getAvailableProducerCodesForReport,
  getCommissionsReportData,
} from 'Agent/Commissions/ducks/a360CommissionsReport';
import {isLoaded} from 'Core/models/Async';
import {DateTime} from 'luxon';
import React, {useCallback, useEffect, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {CommissionsFilter} from '../DataDashboard/CommissionFilters';
import '../DataDashboard/Commissions.scss';
import {A360CommissionSection} from '../CommissionSection';
import {A360CommissionsAlert} from '../Placeholders';

export const CommissionReportFilter = ({isNewYork}: {isNewYork: boolean}) => {
  const {availableProducerCodesForReporting, filter, isProducerFilterLoading} = useSelector(
    (state: ReducerState) => state.a360CommissionsReport
  );

  const showNoInfoAvailableMsg =
    (!isProducerFilterLoading &&
      isLoaded(availableProducerCodesForReporting) &&
      !availableProducerCodesForReporting?.Data?.length) ||
    filter?.selectedProducerCodes?.length === 0;

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getAvailableProducerCodesForReport(isNewYork));
  }, []);

  const producers = isLoaded(availableProducerCodesForReporting)
    ? availableProducerCodesForReporting?.Data?.map(({agentName, agentNumber}) => `${agentNumber} - ${agentName}`)
    : [];

  const handleProducerCodeChange = useCallback((values: string[]) => {
    dispatch(changeSelectedProducerCodesForReporting(values));
  }, []);

  const handleApplyFilter = useCallback(() => {
    dispatch(getCommissionsReportData());
  }, []);

  return (
    <div className="a360-commissions__filters">
      <A360CommissionSection
        align="center"
        className="a360_commissions_reporting__filters-container"
        aria-label="commissions report filter"
      >
        <Typography className="a360-commissions__filters_label">Filter By:</Typography>
        <CommissionsFilter
          handleSelectionChange={handleProducerCodeChange}
          id="producer-codes-filter-reporting"
          isLoading={isProducerFilterLoading}
          filterKey="ProducerCode"
          label="Producer Codes"
          options={producers}
          producerFilterClearAction={clearProducerFilter}
          value={filter.selectedProducerCodes}
        />
        <CommissionReportMonthAndYearFilter />
        <CommissionsReportTypeFilter />
        <Button variant="contained" className="a360-commissions__filters_apply-filter-btn" onClick={handleApplyFilter}>
          Apply Filter
        </Button>
      </A360CommissionSection>

      {showNoInfoAvailableMsg && (
        <div className="a360-commissions__filters-not_available">
          <A360CommissionsAlert message="No Information Available." />
        </div>
      )}
    </div>
  );
};

const CommissionReportMonthAndYearFilter = () => {
  const {filter} = useSelector((state: ReducerState) => state.a360CommissionsReport);

  const currentDate = DateTime.local();

  const dispatch = useDispatch();

  const handleChange = useCallback((newOption: DateTime) => {
    dispatch(changeSelectedDateForReporting(newOption));
  }, []);

  const options = useMemo(() => Array.from({length: 25}, (_, i) => currentDate.minus({months: i})), []);

  return (
    <Autocomplete
      className="a360_commissions_reporting__filters_filter-input"
      id="a360-commissions-reporting-month-year-filter"
      renderInput={params => (
        <TextField {...params} inputProps={{...params.inputProps, 'aria-label': 'Month & Year'}} />
      )}
      options={options}
      getOptionLabel={option => (currentDate.hasSame(option, 'month') ? 'Current Month' : option.toFormat('LLLL yyyy'))}
      value={filter.selectedDate}
      disablePortal={false}
      onChange={(_e, value) => handleChange(value)}
      disableClearable
    />
  );
};

const CommissionsReportTypeFilter = () => {
  const {filter} = useSelector((state: ReducerState) => state.a360CommissionsReport);

  const dispatch = useDispatch();

  const handleSelectedReportChange = useCallback(
    (event: SelectChangeEvent<CommissionsReport>) => {
      const selectedReport = event.target.value as CommissionsReport;
      dispatch(changeSelectedCommissionsReport(selectedReport));
    },
    [dispatch]
  );

  return (
    <Select
      inputProps={{'aria-label': 'commissions report type'}}
      value={filter.selectedReport}
      id="commissions-report-filter"
      onChange={e => handleSelectedReportChange(e)}
      className="a360_commissions_reporting__filters_filter-input"
      fullWidth
      sx={{
        "& .MuiSelect-icon": {
          right: "17px"
        }
      }}
    >
      <MenuItem value={CommissionsReport.EARNED_ACTIVITY}>Earned Activity</MenuItem>
      <MenuItem value={CommissionsReport.ORDINARY}>Ordinary Activity</MenuItem>
    </Select>
  );
};
